import { ColumnType } from "@/components/Table";
import { NOTIFICATION_STYLES } from "@/constants/constants";
import { useDebounce } from "@/hooks/useDebounce";
import { getNotifications } from "@/services";
import { useNotificationStore } from "@/store/notificationStore";
import {
  GetNotificationsParams,
  Notification,
  NOTIFICATION_QUERY_KEYS,
  NotificationStatus,
} from "@/types";
import { formatDate } from "@/utils/formatDate";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import ActionDelete from "../components/ActionDelete";

export const useNotificationTable = () => {
  // Custom hook logic here
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [page, setPage] = useState<number>(1);
  const { openEditModal, setCopyedNotification } = useNotificationStore();

  const columns: ColumnType<Notification>[] = useMemo(() => {
    return [
      {
        title: "通知テーマ",
        dataIndex: "title",
        key: "title",
        isTruncated: true,
        displayTitle: true,
        width: 140,
      },
      {
        title: "通知内容",
        dataIndex: "description",
        key: "description",
        isTruncated: true,
        displayTitle: true,
        width: 140,
      },
      {
        title: "通知日",
        dataIndex: "send_time",
        key: "send_time",
        render: (value: string) => (value ? formatDate(value) : ""),
        // enableSorting: true,
        width: 100,
      },
      {
        title: "通知者",
        dataIndex: "manager",
        key: "manager",
        width: 100,
        isTruncated: true,
        displayTitle: true,
      },
      {
        title: "通知対象",
        dataIndex: "description",
        key: "notification_receiver",
        render: (_, record: Notification) => {
          const notiCompanies = record.send_to_all_companies
            ? []
            : record.notificationsCompany?.map(
                (notiComp) => notiComp.company?.name
              ) || [];
          const notiGroups = record.send_to_all_groups
            ? []
            : record.notificationsGroup?.map(
                (notiGroup) => notiGroup.group?.name
              ) || [];
          const notiStores = record.send_to_all_stores
            ? []
            : record.notificationsStore?.map(
                (notiStore) => notiStore.store?.name
              ) || [];
          const notiUsers = record.send_to_all_users
            ? []
            : record.notificationsUser?.map(
                (notiUser) => notiUser.user?.full_name || notiUser.user_id
              ) || [];

          const sendAllText = [];
          if (record.send_to_all_companies) {
            sendAllText.push("全事業所");
          }
          if (record.send_to_all_groups) {
            sendAllText.push("全グループ");
          }
          if (record.send_to_all_stores) {
            sendAllText.push("全店");
          }
          if (record.send_to_all_users) {
            sendAllText.push("全ユーザー");
          }

          const receiverText = [
            ...notiCompanies,
            ...notiGroups,
            ...notiStores,
            ...notiUsers,
          ].join(", ");

          return (
            <div>
              {sendAllText.length > 0 && (
                <div className="mb-1">{sendAllText.join(", ")}</div>
              )}
              {receiverText.length > 0 && (
                <div className="truncate" title={receiverText}>
                  {receiverText}
                </div>
              )}
            </div>
          );
        },
        isTruncated: true,
        displayTitle: true,
        width: 200,
      },
      {
        title: "状態",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (value: NotificationStatus): any => {
          return (
            <div className="flex items-center gap-2">
              <span className={NOTIFICATION_STYLES[value].className}></span>
              <span>{NOTIFICATION_STYLES[value].title}</span>
            </div>
          );
        },
      },
    ];
  }, []);

  const debounceSearch = useDebounce(searchTerm.trim(), 300);

  // Reset page when filter values change
  useEffect(() => {
    setPage(1);
  }, [filters, debounceSearch]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      NOTIFICATION_QUERY_KEYS.ALL_NOTIFICATIONS,
      debounceSearch,
      filters,
      page,
    ],
    queryFn: () =>
      getNotifications({
        page,
        s: debounceSearch,
        ...filters,
        order: filters.order || "DESC",
        orderBy: filters.orderBy || "created_at",
        limit: 14,
      } as GetNotificationsParams),
  });

  const handleCopy = (
    notification: Notification,
    isDeleteRecivers?: boolean
  ) => {
    setCopyedNotification({
      notification,
      isDeleteRecivers,
    });
  };

  const handleEdit = (notification: Notification) => {
    openEditModal(notification.id);
  };

  const renderAction = (notification: Notification) => {
    if (notification.status !== NotificationStatus.DRAFT) {
      return [
        {
          title: "複製",
          action: () => handleCopy(notification),
        },
      ];
    }

    return [
      {
        title: "複製",
        action: () => handleCopy(notification),
      },
      {
        title: "編集",
        action: () => handleEdit(notification),
      },
      {
        title: <ActionDelete id={notification.id} />,
        enableStopPropagation: true,
        actionClassName: "p-0",
      },
    ];
  };

  // Fix call previous page when current data is empty
  useEffect(() => {
    const pagination = data?.pagination;
    if (!pagination) return;

    if (pagination.page > 1 && pagination.page > pagination.pageCount) {
      setPage(pagination.pageCount);
    }
  }, [data]);

  return {
    searchTerm,
    setSearchTerm,
    columns,
    notifications: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    filters,
    setFilters,
    page,
    handleCopy,
    setPage,
    renderAction,
    refetch,
  };
};
